import { Badge } from '@fluentui/react-components';
import type { BadgeProps } from '@fluentui/react-components';
import type { SubscriptionTier, TierKey } from '@worklogix/shared';

/**
 * Display labels for each tier key, used when only a bare {@link TierKey} is
 * supplied (a full {@link SubscriptionTier} carries its own `name`).
 */
const TIER_LABELS: Record<TierKey, string> = {
  free: 'Free',
  pro: 'Pro',
  enterprise: 'Enterprise',
};

/**
 * Badge color per tier: Free is neutral, Pro carries the brand accent, and
 * Enterprise uses the strongest emphasis. None of these convey an error/blocked
 * state — a tier is always a valid, informational attribute.
 */
const TIER_COLORS: Record<TierKey, NonNullable<BadgeProps['color']>> = {
  free: 'subtle',
  pro: 'brand',
  enterprise: 'important',
};

export interface TierBadgeProps {
  /**
   * The tier to render. Accepts either a bare {@link TierKey} (label derived
   * from the key) or a full {@link SubscriptionTier} (uses its `name`).
   */
  tier: TierKey | SubscriptionTier;
}

/** Narrow the union to its underlying key. */
function tierKeyOf(tier: TierKey | SubscriptionTier): TierKey {
  return typeof tier === 'string' ? tier : tier.key;
}

/** Resolve the display label for the badge. */
function tierLabelOf(tier: TierKey | SubscriptionTier): string {
  return typeof tier === 'string' ? TIER_LABELS[tier] : tier.name;
}

/**
 * A small Fluent {@link Badge} that renders a tenant's subscription tier with a
 * tier-appropriate color. Purely presentational.
 */
export function TierBadge({ tier }: TierBadgeProps): JSX.Element {
  const key = tierKeyOf(tier);
  return (
    <Badge appearance="filled" color={TIER_COLORS[key]} shape="rounded">
      {tierLabelOf(tier)}
    </Badge>
  );
}
